import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { visualConfig } from '../../config/visualConfig';

interface OptimizationSettingsTabProps {
  stackingPattern: string;
  rotationEnabled: boolean;
  thisSideUp: boolean;
  loadBearingCapacity: boolean;
  usePallets: boolean;
  setStackingPatternAndReset: (value: string) => void;
  setRotationEnabledAndReset: (value: boolean) => void;
  setThisSideUpAndReset: (value: boolean) => void;
  setLoadBearingCapacityAndReset: (value: boolean) => void;
  handleOptimize: () => void;
  goToPreviousTab: () => void;
}

export function OptimizationSettingsTab({
  stackingPattern,
  rotationEnabled,
  thisSideUp,
  loadBearingCapacity,
  usePallets,
  setStackingPatternAndReset,
  setRotationEnabledAndReset,
  setThisSideUpAndReset,
  setLoadBearingCapacityAndReset,
  handleOptimize,
  goToPreviousTab
}: OptimizationSettingsTabProps) {
  const colors = visualConfig.colors.settings;
  
  return (
    <Card className={`${visualConfig.layout.card.minHeightSettings} flex flex-col ${visualConfig.layout.card.background} ${visualConfig.layout.card.shape} overflow-hidden`}>
      <CardHeader className={`bg-gradient-to-r ${colors.gradient} text-white ${visualConfig.layout.header.padding}`}>
        <CardTitle className={visualConfig.layout.header.title}>Optimization Settings</CardTitle>
        <CardDescription className={`text-purple-100 ${visualConfig.layout.header.description}`}>
          Configure how the optimization algorithm should behave.
        </CardDescription>
      </CardHeader>
      <CardContent className={`${visualConfig.layout.content.spacing} ${visualConfig.layout.content.padding}`}>
        <div className={visualConfig.interactive.form.spacing}>
          <div className="flex items-center gap-3">
            <Label htmlFor="stacking-pattern-select" className={`${visualConfig.typography.subsectionTitle} ${visualConfig.colors.neutral.text.disabled}`}>Stacking Pattern</Label>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${visualConfig.typography.tiny} font-medium bg-gradient-to-r ${colors.gradient} text-white`}>
              PRO
            </span>
          </div>
          <Select onValueChange={setStackingPatternAndReset} value={stackingPattern} disabled>
            <SelectTrigger id="stacking-pattern-select" className={`${visualConfig.interactive.select.shape} ${visualConfig.interactive.select.disabled}`}>
              <SelectValue placeholder="Select stacking pattern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto-Optimize (Recommended)</SelectItem>
              <SelectItem value="column">Column Stack</SelectItem>
            </SelectContent>
          </Select>
          <p className={`${visualConfig.typography.body} ${visualConfig.colors.neutral.text.muted} ${visualConfig.interactive.form.background} ${visualConfig.interactive.form.padding}`}>
            <span className="font-medium">🚀 PRO Feature:</span> Advanced stacking patterns are available in the professional version. Currently using optimized stacking for maximum efficiency.
          </p>
        </div>

        <div className={visualConfig.interactive.form.spacing}>
          <div className={`${visualConfig.interactive.form.padding} rounded-2xl border ${!usePallets ? 'bg-gray-100 border-gray-300 opacity-50' : visualConfig.interactive.form.background}`}>
            <label className={`flex items-center space-x-4 ${!usePallets ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                checked={rotationEnabled}
                onChange={(e) => setRotationEnabledAndReset(e.target.checked)}
                disabled={!usePallets}
                className={`${visualConfig.interactive.checkbox.size} ${visualConfig.interactive.checkbox.shape} ${!usePallets ? visualConfig.colors.neutral.text.disabled + ' border-gray-300 cursor-not-allowed' : colors.checkbox + ' border-gray-300 focus:ring-purple-500 ' + visualConfig.interactive.checkbox.focus}`}
              />
              <div className="flex-1">
                <span className={`${visualConfig.typography.subsectionTitle} ${!usePallets ? visualConfig.colors.neutral.text.muted : visualConfig.colors.neutral.text.primary}`}>Enable Pallet Rotation</span>
                {!usePallets && (
                  <p className={`${visualConfig.typography.small} ${visualConfig.colors.neutral.text.disabled} mt-1`}>Only available when using pallets</p>
                )}
              </div>
            </label>
          </div>
          
          <div className={`${visualConfig.interactive.form.padding} ${visualConfig.interactive.form.background}`}>
            <label className="flex items-center space-x-4 cursor-pointer">
              <input
                type="checkbox"
                checked={thisSideUp}
                onChange={(e) => setThisSideUpAndReset(e.target.checked)}
                className={`${visualConfig.interactive.checkbox.size} ${colors.checkbox} ${visualConfig.interactive.checkbox.shape} focus:ring-purple-500 ${visualConfig.interactive.checkbox.focus}`}
              />
              <span className={`${visualConfig.typography.subsectionTitle} ${visualConfig.colors.neutral.text.primary}`}>This Side Up (Prevent Vertical Rotation)</span>
            </label>
          </div>
          
          <div className={`${visualConfig.interactive.form.padding} ${visualConfig.interactive.form.background}`}>
            <label className="flex items-center space-x-4 cursor-pointer">
              <input
                type="checkbox"
                checked={loadBearingCapacity}
                onChange={(e) => setLoadBearingCapacityAndReset(e.target.checked)}
                className={`${visualConfig.interactive.checkbox.size} ${colors.checkbox} ${visualConfig.interactive.checkbox.shape} focus:ring-purple-500 ${visualConfig.interactive.checkbox.focus}`}
              />
              <span className={`${visualConfig.typography.subsectionTitle} ${visualConfig.colors.neutral.text.primary}`}>Consider Load Bearing Capacity</span>
            </label>
          </div>
        </div>
      </CardContent>
      <div className={`${visualConfig.layout.footer.layout} ${visualConfig.layout.footer.padding}`}>
        <button
          onClick={goToPreviousTab}
          className={`${visualConfig.interactive.button.base} ${visualConfig.colors.neutral.previous} ${visualConfig.interactive.button.focus} focus:ring-gray-200`}
        >
          Previous
        </button>
        <button
          onClick={handleOptimize}
          className={`${visualConfig.interactive.button.base} ${colors.button} ${visualConfig.interactive.button.focus} ${colors.ring}`}
        >
          Optimize Loading
        </button>
      </div>
    </Card>
  );
} 